import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { articlesAPI, categoriesAPI, pollsAPI, partnersAPI, newsletterAPI, radioAPI, sectionsAPI } from '../lib/api';
import { Clock, Eye, ArrowRight, Crown, Radio, ChevronRight, Send } from 'lucide-react';
import AdSlot from '../components/ads/AdSlot';
import { useI18n } from '../lib/i18n';
import ArticleCoverMedia from '../components/common/ArticleCoverMedia';
import { cleanArticleTitle, getArticleCoverImages, normalizeExternalUrl, resolveMediaUrl, toPlainText } from '../lib/utils';

const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1504711434969-e33886168d8c?w=800';

function ArticleCard({ article, size = 'normal', t = (key, fallback) => fallback }) {
  const safeTitle = cleanArticleTitle(article.title, article);
  const safeExcerpt = toPlainText(article.excerpt || article.subtitle);
  const safeCategory = toPlainText(article.category_name);
  const safeAuthor = toPlainText(article.author_name);
  const hasCoverMedia = getArticleCoverImages(article).length > 0;

  if (size === 'hero') {
    return (
      <Link to={`/article/${article.slug || article.id}`} className="block relative group img-zoom home-hero-card" data-testid="hero-article-link">
        <div className="relative min-h-[300px] aspect-[4/3] sm:min-h-0 sm:aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#1b1b1b] via-[#292929] to-[#101010]">
          <ArticleCoverMedia
            article={article}
            alt={safeTitle}
            fallbackSrc={DEFAULT_HERO_IMAGE}
            className="w-full h-full"
            imageClassName="brightness-[0.72] saturate-[0.82]"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/78 to-transparent home-hero-overlay" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-10">
          {article.is_breaking && (
            <span className="inline-block bg-[#c4170c] text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 mb-3">{t('home.alert', 'Alerte')}</span>
          )}
          {article.is_premium && (
            <span className="inline-block bg-[#D4AF37] text-black text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 mb-3 ml-2">{t('home.premium', 'Premium')}</span>
          )}
          <span className="block text-[#c4170c] text-xs font-bold uppercase tracking-[0.2em] mb-2">{safeCategory}</span>
          <h1 className="text-[2rem] sm:text-[2.5rem] lg:text-[3rem] max-w-[720px] line-clamp-4 md:line-clamp-none font-semibold leading-[1.02] tracking-[-0.03em] text-white mb-2 sm:mb-3" style={{ fontFamily: 'var(--mk-heading-font)' }}>
            {safeTitle}
          </h1>
          <p className="text-gray-400 news-support-sm max-w-2xl mb-3 line-clamp-2 home-hero-excerpt">{safeExcerpt}</p>
          <div className="flex items-center gap-4 text-gray-500 news-support-sm home-hero-meta">
            <span>{safeAuthor}</span>
            <span className="flex items-center gap-1"><Clock size={12} />{article.reading_time} min</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/article/${article.slug || article.id}`} className="block article-card group" data-testid={`article-card-${article.slug || article.id}`}>
      <div className="bg-[#141414] border border-white/5 hover:border-white/10 transition-all">
        {hasCoverMedia && (
          <div className="img-zoom aspect-[16/10] overflow-hidden">
            <ArticleCoverMedia
              article={article}
              alt={safeTitle}
              className="w-full h-full"
              loading="lazy"
            />
          </div>
        )}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[#c4170c] text-[10px] font-bold uppercase tracking-[0.2em]">{safeCategory}</span>
            {article.is_premium && <Crown size={12} className="text-[#D4AF37]" />}
          </div>
          <h3 className="news-title-md font-bold text-white leading-tight mb-2 group-hover:text-[#c4170c] transition-colors line-clamp-2" style={{ fontFamily: 'var(--mk-heading-font)' }}>
            {safeTitle}
          </h3>
          <div className="flex items-center gap-3 text-gray-500 news-support-sm">
            <span className="flex items-center gap-1"><Clock size={11} />{article.reading_time} min</span>
            <span className="flex items-center gap-1"><Eye size={11} />{article.views}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function SectionTitle({ title, link, linkText = "Voir tout" }) {
  return (
    <div className="flex items-center justify-between mb-6 border-b border-white/10 pb-3">
      <h2 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--mk-heading-font)' }}>
        <span className="text-[#c4170c]">|</span> {title}
      </h2>
      {link && (
        <Link to={link} className="text-xs text-gray-500 hover:text-white uppercase tracking-wider flex items-center gap-1 transition-colors">
          {linkText} <ChevronRight size={14} />
        </Link>
      )}
    </div>
  );
}

function PollWidget({ poll, t = (key, fallback) => fallback }) {
  const [voted, setVoted] = useState(false);
  const [data, setData] = useState(poll);

  const handleVote = async (idx) => {
    if (voted) return;
    try {
      await pollsAPI.vote(data.id, idx);
      setVoted(true);
      const updated = await pollsAPI.active();
      const p = (updated.data || []).find(x => x.id === data.id);
      if (p) setData(p);
    } catch {}
  };

  const totalVotes = data.total_votes || 0;

  return (
    <div className="bg-[#141414] border border-white/5 p-6" data-testid="poll-widget">
      <h3 className="text-sm font-bold uppercase tracking-wider text-[#c4170c] mb-4">{t('home.poll', 'Sondage')}</h3>
      <p className="text-white font-semibold mb-4 text-sm" style={{ fontFamily: 'var(--mk-heading-font)' }}>{data.question}</p>
      <div className="space-y-2">
        {data.options?.map((opt, i) => {
          const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
          return (
            <button
              key={i}
              onClick={() => handleVote(i)}
              className={`w-full text-left p-3 border transition-all text-sm relative overflow-hidden ${voted ? 'border-white/10 cursor-default' : 'border-white/10 hover:border-[#c4170c] cursor-pointer'}`}
              data-testid={`poll-option-${i}`}
            >
              {voted && <div className="absolute inset-y-0 left-0 bg-[#c4170c]/10" style={{ width: `${pct}%` }} />}
              <span className="relative z-10 flex items-center justify-between">
                <span className="text-gray-300">{opt.label}</span>
                {voted && <span className="text-[#c4170c] font-bold text-xs">{pct}%</span>}
              </span>
            </button>
          );
        })}
      </div>
      {voted && <p className="text-gray-500 text-xs mt-3">{totalVotes} {t('home.votes', 'votes')}</p>}
    </div>
  );
}

function PartnersCarousel({ partners }) {
  const trackRef = useRef(null);
  // Duplicate for infinite loop
  const items = [...partners, ...partners];

  return (
    <div className="overflow-hidden relative partners-carousel-wrapper py-1">
      <div
        ref={trackRef}
        className="flex gap-5 sm:gap-6 partners-carousel-track"
        style={{ width: 'max-content' }}
      >
        {items.map((p, i) => (
          <a
            key={i}
            href={normalizeExternalUrl(p.url || '') || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="group shrink-0 flex h-[68px] w-[132px] items-center justify-center overflow-hidden rounded-[18px] border border-[#ead8d1] bg-white px-3 shadow-[0_10px_24px_rgba(15,23,42,0.07)] transition-all hover:-translate-y-0.5 hover:border-[#c4170c]/25 hover:shadow-[0_16px_34px_rgba(15,23,42,0.10)] sm:h-[74px] sm:w-[150px]"
          >
            {p.logo ? (
              <span className="flex h-full w-full items-center justify-center overflow-hidden">
                <img
                  src={resolveMediaUrl(p.logo)}
                  alt={p.name}
                  className="max-h-[58px] max-w-[126px] scale-[1.18] object-contain opacity-95 mix-blend-multiply transition-transform duration-300 group-hover:scale-[1.26] sm:max-h-[64px] sm:max-w-[142px]"
                />
              </span>
            ) : (
              <span className="px-2 text-center text-xs font-black uppercase tracking-[0.12em] text-gray-700 transition-colors group-hover:text-[#c4170c]">
                {p.name}
              </span>
            )}
          </a>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const { language, t } = useI18n();
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [popular, setPopular] = useState([]);
  const [categories, setCategories] = useState([]);
  const [polls, setPolls] = useState([]);
  const [partners, setPartners] = useState([]);
  const [radioData, setRadioData] = useState(null);
  const [sections, setSections] = useState([]);
  const [loadSecondaryData, setLoadSecondaryData] = useState(false);
  const [nlEmail, setNlEmail] = useState('');
  const [nlMsg, setNlMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    articlesAPI.featured().then((featuredResult) => {
      if (cancelled) return;
      setFeatured(featuredResult.data || []);
    }).catch(() => {
      if (!cancelled) setFeatured([]);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [language]);

  useEffect(() => {
    if (loading || loadSecondaryData) return undefined;

    const enableSecondaryData = () => setLoadSecondaryData(true);
    const handleScroll = () => {
      if (window.scrollY > 160) enableSecondaryData();
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('pointerdown', enableSecondaryData, { once: true, passive: true });
    window.addEventListener('touchstart', enableSecondaryData, { once: true, passive: true });
    window.addEventListener('keydown', enableSecondaryData, { once: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('pointerdown', enableSecondaryData);
      window.removeEventListener('touchstart', enableSecondaryData);
      window.removeEventListener('keydown', enableSecondaryData);
    };
  }, [loadSecondaryData, loading]);

  useEffect(() => {
    if (loading || !loadSecondaryData) return undefined;

    let cancelled = false;
    Promise.allSettled([
      articlesAPI.latest(12),
      articlesAPI.popular(5),
      categoriesAPI.list(),
      pollsAPI.active(),
      partnersAPI.list(),
      radioAPI.get({ compact: true, limit: 1 }),
      sectionsAPI.get(),
    ]).then(([latestResult, popularResult, categoriesResult, pollsResult, partnersResult, radioResult, sectionsResult]) => {
      if (cancelled) return;
      if (latestResult.status === 'fulfilled') setLatest(latestResult.value.data || []);
      if (popularResult.status === 'fulfilled') setPopular(popularResult.value.data || []);
      if (categoriesResult.status === 'fulfilled') setCategories(categoriesResult.value.data || []);
      if (pollsResult.status === 'fulfilled') setPolls(pollsResult.value.data || []);
      if (partnersResult.status === 'fulfilled') setPartners(partnersResult.value.data || []);
      if (radioResult.status === 'fulfilled') setRadioData(radioResult.value.data);
      if (sectionsResult.status === 'fulfilled') setSections(sectionsResult.value.data || []);
    });

    return () => {
      cancelled = true;
    };
  }, [language, loadSecondaryData, loading]);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try {
      await newsletterAPI.subscribe({ email: nlEmail });
      setNlMsg(t('home.newsletter_success', 'Inscription réussie !'));
      setNlEmail('');
    } catch { setNlMsg(t('home.newsletter_error', "Erreur. Réessayez.")); }
  };

  const heroArticle = featured[0];
  const secondaryFeatured = featured.slice(1, 4);
  const categorySections = useMemo(() => {
    if (categories.length) return categories;
    const seen = new Set();
    return latest.reduce((acc, article) => {
      const slug = article.category_slug;
      const name = article.category_name;
      if (!slug || !name || seen.has(slug)) return acc;
      seen.add(slug);
      acc.push({ slug, name });
      return acc;
    }, []);
  }, [categories, latest]);

  // Helper: check if a section is enabled (defaults to true if no config loaded)
  const isSectionEnabled = (key) => {
    if (!sections.length) return true;
    const s = sections.find(x => x.key === key);
    return s ? s.enabled !== false : true;
  };
  const featuredEpisode = Array.isArray(radioData?.episodes) ? radioData.episodes[0] : null;

  if (loading) {
    return (
      <div className="max-w-[1200px] mx-auto px-4 py-12">
        <div className="skeleton-shimmer h-[500px] mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="skeleton-shimmer h-64" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in-up">
      {/* Hero Section */}
      {isSectionEnabled('hero') && <section className="max-w-[1200px] mx-auto px-4 pt-6 pb-8" data-testid="hero-section">
        {heroArticle ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-8">
              <ArticleCard article={heroArticle} size="hero" t={t} />
            </div>
            <div className="lg:col-span-4 flex flex-col gap-4">
              {secondaryFeatured.map((a) => {
                const cardTitle = cleanArticleTitle(a.title, a);
                const cardCategory = toPlainText(a.category_name);
                return (
                  <Link key={a.id} to={`/article/${a.slug || a.id}`} className="block bg-[#141414] border border-white/5 hover:border-white/10 transition-all group p-4 flex-1" data-testid={`secondary-${a.slug || a.id}`}>
                    <span className="text-[#c4170c] text-[10px] font-bold uppercase tracking-[0.2em]">{cardCategory}</span>
                    <h3 className="news-title-md font-bold text-white mt-2 leading-tight group-hover:text-[#c4170c] transition-colors line-clamp-3" style={{ fontFamily: 'var(--mk-heading-font)' }}>
                      {cardTitle}
                    </h3>
                    <span className="text-gray-500 news-support-sm mt-2 flex items-center gap-1"><Clock size={11} />{a.reading_time} min</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="h-[400px] bg-[#141414] border border-white/5 flex items-center justify-center text-gray-500">
            {t('home.no_featured', 'Aucun article en vedette')}
          </div>
        )}
      </section>}

      <section className="max-w-[1200px] mx-auto px-4 pb-2">
        <AdSlot
          slot="home-hero"
          adSenseSlot={process.env.REACT_APP_ADSENSE_SLOT_HOME_HERO || ''}
          minHeight={160}
          title={t('home.ad_featured', 'Publicité en vedette')}
        />
      </section>

      {/* Latest Articles */}
      {isSectionEnabled('latest') && <section className="max-w-[1200px] mx-auto px-4 py-8" data-testid="latest-section">
        <SectionTitle title={t('home.latest_news', 'Dernières Nouvelles')} link="/categorie/actualites" linkText={t('home.view_all', 'Voir tout')} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {latest.slice(0, 8).map(a => (
            <ArticleCard key={a.id} article={a} t={t} />
          ))}
        </div>
      </section>}

      <section className="max-w-[1200px] mx-auto px-4 pb-6">
        <AdSlot
          slot="sidebar"
          adSenseSlot={process.env.REACT_APP_ADSENSE_SLOT_HOME_INLINE || ''}
          minHeight={120}
        />
      </section>

      {/* Popular + Poll + Radio */}
      {isSectionEnabled('popular') && <section className="max-w-[1200px] mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Popular */}
          <div className="lg:col-span-5">
            <SectionTitle title={t('home.most_read', 'Les Plus Lues')} />
            <div className="space-y-3">
              {popular.map((a, i) => {
                const itemTitle = cleanArticleTitle(a.title, a);
                const itemCategory = toPlainText(a.category_name);
                return (
                  <Link key={a.id} to={`/article/${a.slug || a.id}`} className="flex items-start gap-4 group p-3 bg-[#141414] border border-white/5 hover:border-white/10 transition-all" data-testid={`popular-${i}`}>
                    <span className="text-3xl font-black text-[#c4170c]/20 shrink-0 w-10 text-center" style={{ fontFamily: 'var(--mk-heading-font)' }}>{i + 1}</span>
                    <div>
                      <span className="text-[10px] text-[#c4170c] font-bold uppercase tracking-[0.2em]">{itemCategory}</span>
                      <h4 className="news-title-md font-semibold text-white group-hover:text-[#c4170c] transition-colors line-clamp-2 mt-1">{itemTitle}</h4>
                      <span className="news-support-sm text-gray-500 flex items-center gap-1 mt-1"><Eye size={11} />{a.views} {t('home.views', 'vues')}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right column: Poll + Radio */}
          <div className="lg:col-span-4 space-y-6">
            {polls[0] && <PollWidget poll={polls[0]} t={t} />}
            <AdSlot slot="header" adSenseSlot={process.env.REACT_APP_ADSENSE_SLOT_HOME_SIDEBAR || ''} minHeight={140} />
            
            {/* Radio */}
            <Link to="/radio" className="block bg-[#141414] border border-white/5 p-6 hover:border-[#c4170c]/30 transition-all group" data-testid="home-radio-block">
              <div className="flex items-center gap-2 mb-3">
                <Radio size={16} className="text-[#c4170c]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#c4170c]">{t('home.radio_live', 'Podcast en video')}</span>
                {radioData?.stream?.is_live && <span className="w-2 h-2 bg-[#c4170c] rounded-full pulse-red ml-auto"></span>}
              </div>
              <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'var(--mk-heading-font)' }}>
                {radioData?.stream?.name || 'MAKAYA ENFO Podcast'}
              </h3>
              <p className="text-gray-500 text-xs">
                {featuredEpisode?.title || radioData?.stream?.description || t('home.radio_desc', 'Regardez les épisodes les plus récents.')}
              </p>
              <div className="mt-4 bg-[#c4170c] text-white text-center py-2 text-xs font-bold uppercase tracking-wider group-hover:bg-[#a11008] transition-colors">
                {t('home.listen_now', 'Regarder maintenant')}
              </div>
            </Link>
          </div>

          {/* CTA Premium */}
          <div className="lg:col-span-3">
            <div className="bg-[#1A1713] border border-[#D4AF37]/20 p-6 gold-glow" data-testid="premium-cta">
              <Crown size={24} className="text-[#D4AF37] mb-4" />
              <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'var(--mk-heading-font)' }}>{t('home.go_premium', 'Passez Premium')}</h3>
              <p className="text-gray-400 text-sm mb-4">{t('home.premium_desc', 'Accédez à du contenu exclusif, analyses approfondies et bien plus encore.')}</p>
              <ul className="space-y-2 mb-6">
                {[t('home.premium_feature_1', 'Articles exclusifs'), t('home.premium_feature_2', 'Sans publicité'), t('home.premium_feature_3', 'Newsletter premium')].map(f => (
                  <li key={f} className="text-xs text-gray-400 flex items-center gap-2">
                    <div className="w-1 h-1 bg-[#D4AF37]" />{f}
                  </li>
                ))}
              </ul>
              <Link to="/plans" className="block bg-[#D4AF37] text-black text-center py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#B8972E] transition-colors">
                {t('home.view_plans', 'Voir les plans')}
              </Link>
            </div>
          </div>
        </div>
      </section>}

      {/* Category Blocks */}
      {isSectionEnabled('categories') && categorySections.slice(0, 4).map(cat => {
        const catArticles = latest.filter(a => a.category_slug === cat.slug).slice(0, 4);
        if (!catArticles.length) return null;
        return (
          <section key={cat.slug} className="max-w-[1200px] mx-auto px-4 py-8" data-testid={`cat-section-${cat.slug}`}>
            <SectionTitle title={cat.name} link={`/categorie/${cat.slug}`} linkText={t('home.view_all', 'Voir tout')} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {catArticles.map(a => <ArticleCard key={a.id} article={a} t={t} />)}
            </div>
          </section>
        );
      })}

      {/* Newsletter */}
      {isSectionEnabled('newsletter') && <section className="bg-[#141414] border-y border-white/5 py-12" data-testid="newsletter-section">
        <div className="max-w-[600px] mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: 'var(--mk-heading-font)' }}>
            {t('home.stay', 'Restez')} <span className="text-[#c4170c]">{t('home.informed', 'Informé')}</span>
          </h2>
          <p className="text-gray-500 text-sm mb-6">{t('home.newsletter_desc', 'Recevez les dernières nouvelles directement dans votre boîte mail.')}</p>
          <form onSubmit={handleNewsletter} className="flex gap-2">
            <input
              type="email"
              value={nlEmail}
              onChange={e => setNlEmail(e.target.value)}
              placeholder={t('home.email_placeholder', 'Votre adresse email')}
              required
              className="flex-1 bg-[#0A0A0A] border border-white/10 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#c4170c] text-sm"
              data-testid="newsletter-email-input"
            />
            <button type="submit" className="bg-[#c4170c] text-white px-6 py-3 font-bold uppercase text-xs tracking-wider hover:bg-[#a11008] transition-colors flex items-center gap-2" data-testid="newsletter-submit-btn">
              <Send size={14} /> {t('home.subscribe', "S'inscrire")}
            </button>
          </form>
          {nlMsg && <p className="text-green-400 text-sm mt-3">{nlMsg}</p>}
        </div>
      </section>}

      {/* Vous Reporter CTA */}
      {isSectionEnabled('reporter') && <section className="max-w-[1200px] mx-auto px-4 py-12 home-reporter-section" data-testid="vous-reporter-cta">
        <div className="relative overflow-hidden border border-white/5 home-reporter-shell">
          <img src="https://images.pexels.com/photos/14494412/pexels-photo-14494412.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Vous Reporter" className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" decoding="async" />
          <div className="relative bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/90 to-transparent p-8 lg:p-12 home-reporter-overlay">
            <span className="text-[#c4170c] text-xs font-bold uppercase tracking-[0.2em]">{t('home.citizen_journalism', 'Journalisme Citoyen')}</span>
            <h2 className="text-2xl lg:text-3xl font-bold mt-2 mb-3" style={{ fontFamily: 'var(--mk-heading-font)' }}>
              {t('home.become', 'Devenez')} <span className="text-[#c4170c]">{t('home.reporter', 'Vous Reporter')}</span>
            </h2>
            <p className="text-gray-400 text-sm max-w-md mb-6">{t('home.reporter_desc', "Partagez vos informations, photos et vidéos. Contribuez à l'information de proximité.")}</p>
            <Link to="/vous-reporter" className="inline-block bg-white text-black px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-gray-200 transition-colors" data-testid="vous-reporter-btn">
              {t('home.submit_report', 'Soumettre un Rapport')} <ArrowRight size={14} className="inline ml-1" />
            </Link>
          </div>
        </div>
      </section>}

      {/* Partners Carousel */}
      {isSectionEnabled('partners') && partners.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-4 py-8" data-testid="partners-section">
          <SectionTitle title={t('home.partners', 'Nos Partenaires')} />
          <PartnersCarousel partners={partners} />
        </section>
      )}

      <section className="max-w-[1200px] mx-auto px-4 pb-8">
        <AdSlot slot="footer" adSenseSlot={process.env.REACT_APP_ADSENSE_SLOT_FOOTER || ''} minHeight={120} />
      </section>
    </div>
  );
}
